import React, { useState, useEffect } from "react";
import { Column } from "@amzn/meridian";
import StepRenderer from "../StepRenderer";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { setCurrentIterateStep, setIterationProgress } from "src/store/workflowSlice";
import { setScannedBarcode } from "src/store/inspectionCaptureSlice";
import { CameraStreamProps } from "../types/cameraStreamProps";

interface IterateStepProps {
  iterateSteps: any[];
  cameraStreamProps: CameraStreamProps;
  /** Ends the open-ended container loop (surfaced on the barcode sub-step). */
  onFinish?: () => void;
}

const IterateStep: React.FC<IterateStepProps> = ({
  iterateSteps,
  cameraStreamProps,
  onFinish,
}) => {
  const dispatch = useAppDispatch();
  const { resumeIterateStepIndex, resumeCompletedContainerCount } = useAppSelector((s) => s.workflow);
  const [currentIteration, setCurrentIteration] = useState(resumeCompletedContainerCount);
  const [currentStepIndex, setCurrentStepIndex] = useState(resumeIterateStepIndex);

  const currentStep = iterateSteps[currentStepIndex];

  // Update Redux with the current iterate step whenever it changes
  useEffect(() => {
    dispatch(setCurrentIterateStep(currentStep));
  }, [currentStepIndex, dispatch, currentStep]);

  // Update Redux with the current container number so the header can show it.
  // The loop is open-ended, so `total` just reflects the count reached so far.
  useEffect(() => {
    dispatch(setIterationProgress({
      currentIndex: currentIteration,
      total: currentIteration + 1,
    }));
  }, [currentIteration, dispatch]);

  const handleStepComplete = () => {
    // Move to next step in the iteration
    // dispatching the current iterate step lets the WorkflowExecutor 
    // decide whether to keep the camera running or not.
    if (currentStepIndex < iterateSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      dispatch(setCurrentIterateStep(iterateSteps[nextIndex]));
      setCurrentStepIndex(nextIndex);
    } else {
      // Finished all sub-steps for this container. Automatically start the next
      // container — the loop is open-ended and only ends when the user taps
      // "All Containers Finished" (handled by WorkflowExecutor).
      dispatch(setCurrentIterateStep(iterateSteps[0]));
      dispatch(setScannedBarcode(null));
      setCurrentIteration(currentIteration + 1);
      setCurrentStepIndex(0);
    }
  };

  return (
    <Column>
      <StepRenderer
        step={currentStep}
        onStepComplete={handleStepComplete}
        cameraStreamProps={cameraStreamProps}
        onFinish={onFinish}
      />
    </Column>
  );
};

export default IterateStep;
